import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import {
  Icon24BGCAvailable,
  Icon24Transportation,
  Icon24ChevronSmallRight,
} from '@care/react-embrace-icons';
import { Tag } from '@care/react-component-lib';
import { SAFETY_CRIMINAL_RECORDS_PAGE_URL, SAFETY_MOTOR_RECORDS_PAGE_URL } from '@/constants';
import { AnalyticsHelper } from '@/utilities/AnalyticsHelper';
import { CtaText, AnalyticsEventName, CtaLocation, ScreenName } from '@/types/enums';

const navigateWithAnalytics = (url: string) => {
  AnalyticsHelper.logEvent({
    name: AnalyticsEventName.CTA_INTERACTED,
    data: {
      cta_text: CtaText.LEARN_MORE,
      cta_location: CtaLocation.SAFETY,
      screen_name: ScreenName.PLANS_AND_PRICING,
      vertical: 'Unknown',
      enrollment_initiated: false,
    },
  });

  window.location.href = url;
};

type SafetyCheck = {
  id: string;
  icon: React.ElementType;
  title: string;
  description: string;
  onClick: () => void;
};

export const getSafetyChecks = (): SafetyCheck[] => [
  {
    id: 'criminal-check',
    icon: Icon24BGCAvailable,
    title: 'Run additional background checks',
    description:
      'Purchase an Enhanced Background Check to get a dedicated report with the most up-to-date criminal records.',
    onClick: () => navigateWithAnalytics(SAFETY_CRIMINAL_RECORDS_PAGE_URL),
  },
  {
    id: 'motor-check',
    icon: Icon24Transportation,
    title: 'Unlock their driving history',
    description:
      'Want driving records too? Add a Motor Vehicle Records Check to your Enhanced Background Check.',
    onClick: () => navigateWithAnalytics(SAFETY_MOTOR_RECORDS_PAGE_URL),
  },
];

const Safety = () => {
  const theme = useTheme();
  const safetyChecks = getSafetyChecks();

  return (
    <Box
      id="safety"
      data-testid="safety-section"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        pt: { xs: 3, md: 6 },
        pb: { xs: 8, md: 10 },
        px: 2,
        backgroundColor: 'white',
      }}>
      <Typography
        variant="display3"
        sx={{
          textAlign: 'center',
          maxWidth: { xs: 500, lg: 580 },
        }}>
        Access additional safety checks with Premium
      </Typography>
      <Stack
        sx={{
          alignItems: { xs: 'center', md: 'flex-end' },
          justifyContent: 'center',
        }}
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1, md: 10 }}>
        {safetyChecks.map(({ id, icon: Icon, title, description, onClick }) => (
          <Box
            key={id}
            data-testid={id}
            sx={{
              py: 3,
              width: { sm: 400 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}>
            <Icon
              sx={{
                width: 48,
                height: 48,
                mb: 2,
              }}
              color={theme.palette.icon.brand}
            />
            <Typography
              variant="h2"
              sx={{
                mb: { xs: 0.5 },
              }}>
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                fontSize: { sm: '16px' },
                lineHeight: '22px',
                maxWidth: '345px',
              }}
              pt={1.5}>
              {description}
            </Typography>
            <Tag
              careColor="neutral"
              sx={{
                mt: 2,
                mb: 2,
              }}>
              <Typography
                variant="tag"
                component="span"
                sx={{
                  textTransform: 'none',
                  letterSpacing: 0,
                }}>
                Available for purchase
              </Typography>
            </Tag>
            <Button
              sx={{
                fontWeight: 'bold',
                '& .MuiButton-endIcon': { margin: 0 },
                textDecoration: 'underline',
              }}
              size="small"
              variant="text"
              color="primary"
              endIcon={
                <Icon24ChevronSmallRight
                  sx={{
                    mt: '2px',
                    width: 24,
                  }}
                />
              }
              onClick={onClick}>
              {CtaText.LEARN_MORE}
            </Button>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default Safety;
